import { formatIN } from '../kit'
import { useArrived, useTooltip, type TipRow } from '../charts'

// Open findings by severity, and by how long they have been open.
//
// Severity is ordered, so the four severities are four steps of the one risk
// ramp the band colours already use (--hc-b-low to --hc-b-crit), light for low
// and deep for critical, in both themes. Both charts share one axis each, a
// count from zero; a stacked row keeps a 2px surface gap between its segments,
// every count is written as text in the text inks beside the bars, and the
// legend names each severity, so no number lives only in a colour or a tooltip.

export type Severities = { critical: number; high: number; medium: number; low: number }

export const SEVERITIES: { key: keyof Severities; label: string; color: string }[] = [
  { key: 'critical', label: 'Critical', color: 'var(--hc-b-crit)' },
  { key: 'high', label: 'High', color: 'var(--hc-b-high)' },
  { key: 'medium', label: 'Medium', color: 'var(--hc-b-med)' },
  { key: 'low', label: 'Low', color: 'var(--hc-b-low)' },
]

const BUCKET_WORDS: Record<string, string> = {
  '0-30': '0-30 days',
  '31-60': '31-60 days',
  '61-90': '61-90 days',
  '90+': 'Over 90 days',
}

function total(s: Severities): number {
  return s.critical + s.high + s.medium + s.low
}

function niceTop(v: number): number {
  if (v <= 4) return 4
  const step = Math.pow(10, Math.floor(Math.log10(v)))
  for (const m of [1, 2, 2.5, 5, 10]) {
    if (m * step >= v) return m * step
  }
  return 10 * step
}

export function SeverityKey() {
  return (
    <div className="hc-keyrow rpt-key">
      {SEVERITIES.map((s) => (
        <span key={s.key}>
          <i style={{ background: s.color }} />
          {s.label}
        </span>
      ))}
    </div>
  )
}

// One bar per severity on a shared count axis.
export function SeverityBars({ counts, title }: { counts: Severities; title: string }) {
  const arrived = useArrived()
  const tip = useTooltip()
  const top = niceTop(Math.max(0, ...SEVERITIES.map((s) => counts[s.key])))
  return (
    <div className="rpt-bars" role="img" aria-label={title + ': ' + SEVERITIES.map((s) => s.label + ' ' + counts[s.key]).join(', ')}>
      {SEVERITIES.map((s) => {
        const n = counts[s.key]
        const rows: TipRow[] = [{ label: s.label, value: formatIN(n), color: s.color }]
        return (
          <div
            key={s.key}
            className="rpt-bar-row"
            onPointerMove={(e) => tip.show(e, title, rows)}
            onPointerLeave={() => tip.hide()}
          >
            <span className="rpt-bar-l">{s.label}</span>
            <span className="rpt-bar-track">
              <i style={{ width: arrived && n > 0 ? (n / top) * 100 + '%' : 0, background: s.color }} />
            </span>
            <b className="hc-num">{formatIN(n)}</b>
          </div>
        )
      })}
      <Axis top={top} />
    </div>
  )
}

// One stacked row per ageing bucket, on one count axis.
export function AgeingBars({ buckets, title }: { buckets: (Severities & { bucket: string })[]; title: string }) {
  const arrived = useArrived()
  const tip = useTooltip()
  const top = niceTop(Math.max(0, ...buckets.map(total)))
  return (
    <div
      className="rpt-bars"
      role="img"
      aria-label={
        title +
        ': ' +
        buckets.map((b) => (BUCKET_WORDS[b.bucket] ?? b.bucket) + ' ' + total(b) + ' (' + SEVERITIES.map((s) => b[s.key] + ' ' + s.label.toLowerCase()).join(', ') + ')').join('; ')
      }
    >
      {buckets.map((b) => {
        const all = total(b)
        const rows: TipRow[] = SEVERITIES.map((s) => ({ label: s.label, value: formatIN(b[s.key]), color: s.color }))
        const words = BUCKET_WORDS[b.bucket] ?? b.bucket
        return (
          <div key={b.bucket} className="rpt-bar-row" onPointerMove={(e) => tip.show(e, words, rows)} onPointerLeave={() => tip.hide()}>
            <span className="rpt-bar-l">{words}</span>
            <span className="rpt-bar-track stack">
              <span className="rpt-stack" style={{ width: arrived && all > 0 ? (all / top) * 100 + '%' : 0 }}>
                {SEVERITIES.filter((s) => b[s.key] > 0).map((s) => (
                  <i key={s.key} style={{ flexGrow: b[s.key], background: s.color }} />
                ))}
              </span>
            </span>
            <b className="hc-num">{formatIN(all)}</b>
          </div>
        )
      })}
      <Axis top={top} />
    </div>
  )
}

function Axis({ top }: { top: number }) {
  const marks = [0, top / 2, top]
  return (
    <div className="rpt-bar-row rpt-axis" aria-hidden="true">
      <span />
      <span className="rpt-axis-scale">
        {marks.map((m, i) => (
          <span key={i} style={{ left: (i / 2) * 100 + '%' }}>
            {Number.isInteger(m) ? formatIN(m) : m.toFixed(1)}
          </span>
        ))}
      </span>
      <span />
    </div>
  )
}
